<template>
	<ViewFeedItem class="view-feed-contentlatest" v-if="contentModel && contentModel.id">
		<template v-slot:header>
			<ViewLayoutModuleHeader v-bind:title="title" v-bind:subtitle="subtitle">
				<RouterLink v-bind:to="{ name: 'PageContent' }">
					<strong>View All</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<article class="hero-card" v-on:click="Handle_OnClickArticle">
				<!-- Hero imagery -->
				<div class="thumbnail" v-if="contentModel.getThumbnailUrl()">
					<img v-bind:alt="contentModel.getTitle()" v-bind:src="contentModel.getThumbnailUrl()" />
				</div>

				<!-- Article meta -->
				<div class="details">
					<h3 class="title">{{ contentModel.getTitle() }}</h3>

					<p class="excerpt" v-if="excerpt">{{ excerpt }}</p>

					<div class="source-line">
						<img
							class="source-icon"
							v-bind:alt="contentModel.getTitle()"
							v-bind:src="contentModel.getIconUrl()"
							v-if="contentModel.hasIcon()"
						/>
						<span class="date">{{ formattedDate }}</span>
					</div>
				</div>
			</article>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Hero feed module that surfaces the single most recent news article.
	 * Tap navigates to the full content detail page.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedContentLatest extends ViewBase {
		/**
		 * Trimmed excerpt pulled from the article body
		 *
		 * @return string
		 */
		protected get excerpt(): string {
			const raw: string = (this.contentModel.getContent() || '') as string;
			const stripped: string = raw
				.replace(/<[^>]*>/g, ' ')
				.replace(/\s+/g, ' ')
				.trim();

			if (!stripped) {
				return '';
			}

			return stripped.length > 160 ? stripped.slice(0, 157) + '...' : stripped;
		}

		/**
		 * Returns relative time for recent content, formatted date for older items
		 *
		 * @return string
		 */
		protected get formattedDate(): string {
			if (!this.contentModel?.id) {
				return '';
			}

			if (this.contentModel.isMonthOld('published_at')) {
				return this.contentModel.getPublishedAt('MMM D, YYYY');
			}

			return this.contentModel.getPublishedAtTimeAgo();
		}

		/**
		 * Subtitle copy beneath the module header
		 *
		 * @return string
		 */
		protected get subtitle(): string {
			return ''; // 'Hot off the presses';
		}

		/**
		 * Title copy in the module header
		 *
		 * @return string
		 */
		protected get title(): string {
			return 'The Latest';
		}

		/**
		 * Source collection used to pull the most recent news item
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 1 }).news(),
		})
		public contentCollection!: ChalkySticks.Collection.Content;

		/**
		 * Resolved single content model that gets rendered
		 *
		 * @type ChalkySticks.Model.Content
		 */
		protected contentModel: ChalkySticks.Model.Content = ChalkySticks.Factory.Content.model();

		/**
		 * Fetch and select the latest article on mount.
		 *
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			await this.contentCollection.fetch();

			if (this.contentCollection.length > 0) {
				this.contentModel = this.contentCollection.first();
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickArticle(e: Event): Promise<void> {
			e.preventDefault();

			if (!this.contentModel?.id) {
				return;
			}

			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: this.contentModel.getSlug(),
				},
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.view-feed-contentlatest {
		.title {
			font-size: 1.2rem;
		}

		.body {
			padding: 0 var(--padding) var(--padding);
		}

		.hero-card {
			background: var(--chalky-blue-4);
			border-radius: 0.5rem;
			cursor: pointer;
			display: flex;
			flex-direction: column;
			overflow: hidden;
			transition: box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
		}

		.thumbnail {
			aspect-ratio: 16 / 9;
			background: var(--chalky-blue-3);
			overflow: hidden;
			position: relative;

			img {
				height: 100%;
				object-fit: cover;
				width: 100%;
			}
		}

		.details {
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
			padding: 1rem;
		}

		.title {
			font-size: 1.125rem;
			font-weight: var(--font-weight-semibold);
			line-height: 1.3;
		}

		.excerpt {
			color: var(--chalky-grey-2);
			font-size: 0.9rem;
			line-height: 1.4;
		}

		.source-line {
			align-items: center;
			color: var(--chalky-grey-2);
			display: flex;
			font-size: 0.8rem;
			gap: 0.5rem;

			.source-icon {
				border-radius: 50%;
				height: 1rem;
				width: 1rem;
			}
		}
	}

	// User Interaction
	// ---------------------------------------------------------------------------

	.view-feed-contentlatest .hero-card:active {
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
		transform: translateY(-2px);
	}
</style>
